import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Coffee, Wifi, Lock, ClipboardList, UtensilsCrossed, Sun, Cloud, CloudSun, CloudRain, CloudSnow, CloudLightning, CloudFog, ArrowRight, Gamepad2, Ban, MapPin, Clock, Phone, Euro } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { api } from "../lib/api";

const LANGS = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "nl", label: "Nederlands", flag: "🇳🇱" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "zh-CN", label: "中文", flag: "🇨🇳" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "ko", label: "한국어", flag: "🇰🇷" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "tr", label: "Türkçe", flag: "🇹🇷" },
  { code: "sv", label: "Svenska", flag: "🇸🇪" },
  { code: "da", label: "Dansk", flag: "🇩🇰" },
  { code: "no", label: "Norsk", flag: "🇳🇴" },
  { code: "fi", label: "Suomi", flag: "🇫🇮" },
  { code: "cs", label: "Čeština", flag: "🇨🇿" },
  { code: "el", label: "Ελληνικά", flag: "🇬🇷" },
  { code: "hu", label: "Magyar", flag: "🇭🇺" },
  { code: "ro", label: "Română", flag: "🇷🇴" },
  { code: "uk", label: "Українська", flag: "🇺🇦" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
  { code: "th", label: "ไทย", flag: "🇹🇭" },
  { code: "id", label: "Bahasa Indonesia", flag: "🇮🇩" },
  { code: "vi", label: "Tiếng Việt", flag: "🇻🇳" },
  { code: "he", label: "עברית", flag: "🇮🇱" },
  { code: "hr", label: "Hrvatski", flag: "🇭🇷" },
  { code: "sk", label: "Slovenčina", flag: "🇸🇰" },
  { code: "bg", label: "Български", flag: "🇧🇬" },
];

const T = {
  fr: { welcome: "Bienvenue", breakfast: "Petit-déjeuner", week: "Du lundi au vendredi", weekend: "Samedi et dimanche", wifi: "Wi-Fi", pass: "Mot de passe", door: "Code porte d'entrée", important: "Très important", reminders: "Rappels importants", supplements: "Suppléments & tarifs", dining: "Restauration sur place", order: "Commander repas & boissons", weather: "Météo des prochains jours", welcomeMsg: "Toute l'équipe vous souhaite un excellent séjour.", games: "Salle de jeux", noFood: "Nourriture extérieure interdite", chooseLang: "Choisissez votre langue", nearby: "À faire dans les environs", practical: "Infos pratiques", checkout: "Départ au plus tard", contact: "Une question ? Contactez-nous", respect: "Cette maison est un lieu de calme et de partage. Pour que chacun s'y sente bien, merci de prendre soin des lieux et de respecter ces quelques règles. 🙏" },
  en: { welcome: "Welcome", breakfast: "Breakfast", week: "Monday to Friday", weekend: "Saturday & Sunday", wifi: "Wi-Fi", pass: "Password", door: "Front door code", important: "Very important", reminders: "House reminders", dining: "On-site dining", order: "Order meals & drinks", weather: "Weather forecast", welcomeMsg: "The whole team wishes you a wonderful stay.", games: "Games room", noFood: "No outside food or drinks", chooseLang: "Choose your language" },
  es: { welcome: "Bienvenidos", breakfast: "Desayuno", week: "De lunes a viernes", weekend: "Sábado y domingo", wifi: "Wi-Fi", pass: "Contraseña", door: "Código de la puerta", important: "Muy importante", reminders: "Recordatorios", dining: "Restauración in situ", order: "Pedir comidas y bebidas", weather: "Previsión del tiempo", welcomeMsg: "Todo el equipo le desea una estancia excelente.", games: "Sala de juegos", noFood: "Prohibido traer comida o bebida del exterior", chooseLang: "Elija su idioma" },
  de: { welcome: "Willkommen", breakfast: "Frühstück", week: "Montag bis Freitag", weekend: "Samstag & Sonntag", wifi: "WLAN", pass: "Passwort", door: "Türcode", important: "Sehr wichtig", reminders: "Hinweise", dining: "Verpflegung vor Ort", order: "Speisen & Getränke bestellen", weather: "Wettervorhersage", welcomeMsg: "Das gesamte Team wünscht Ihnen einen angenehmen Aufenthalt.", games: "Spielzimmer", noFood: "Mitgebrachte Speisen und Getränke verboten", chooseLang: "Wählen Sie Ihre Sprache" },
  it: { welcome: "Benvenuti", breakfast: "Colazione", week: "Dal lunedì al venerdì", weekend: "Sabato e domenica", wifi: "Wi-Fi", pass: "Password", door: "Codice porta", important: "Molto importante", reminders: "Promemoria", dining: "Ristorazione in loco", order: "Ordina pasti e bevande", weather: "Previsioni meteo", welcomeMsg: "Tutto lo staff vi augura un ottimo soggiorno.", games: "Sala giochi", noFood: "Vietato portare cibo o bevande dall'esterno", chooseLang: "Scegli la tua lingua" },
  nl: { welcome: "Welkom", breakfast: "Ontbijt", week: "Maandag t/m vrijdag", weekend: "Zaterdag & zondag", wifi: "Wifi", pass: "Wachtwoord", door: "Deurcode", important: "Zeer belangrijk", reminders: "Herinneringen", dining: "Eten ter plaatse", order: "Maaltijden & drankjes bestellen", weather: "Weersverwachting", welcomeMsg: "Het hele team wenst u een fijn verblijf.", games: "Speelkamer", noFood: "Geen eigen eten of drinken toegestaan", chooseLang: "Kies uw taal" },
};

const DEFAULT_SUPPLEMENTS = [
  "Un lit bébé peut être fourni au tarif de 10 € par jour.",
  "Toute personne non déclarée dans la réservation sera facturée 20 € par nuit par adulte et 10 € par nuit par enfant.",
];

function weatherIcon(code) {
  if (code === 0) return Sun;
  if (code <= 3) return CloudSun;
  if (code === 45 || code === 48) return CloudFog;
  if (code >= 71 && code <= 77) return CloudSnow;
  if (code >= 85 && code <= 86) return CloudSnow;
  if (code >= 95) return CloudLightning;
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return CloudRain;
  return Cloud;
}

export default function LivretPage() {
  const { settings } = useSite();
  const L = useMemo(() => settings.livret || {}, [settings.livret]);
  const [weather, setWeather] = useState([]);
  const [curLang, setCurLang] = useState("fr");
  const lang = "fr";
  const t = T[lang] || T.fr;
  const loc = (LANGS.find((l) => l.code === lang) || {}).loc || "fr-FR";

  useEffect(() => { api.get("/livret/weather").then((r) => setWeather(r.data.days || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (document.getElementById("goog-te-script")) return;
    window.googleTranslateElementInit = () => {
      if (window.google && window.google.translate) {
        new window.google.translate.TranslateElement({ pageLanguage: "fr", autoDisplay: false }, "google_translate_element");
      }
    };
    const s = document.createElement("script");
    s.id = "goog-te-script";
    s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    document.body.appendChild(s);
  }, []);

  const translateTo = (code) => {
    setCurLang(code);
    let tries = 0;
    const apply = () => {
      const combo = document.querySelector(".goog-te-combo");
      if (combo) { combo.value = code; combo.dispatchEvent(new Event("change")); }
      else if (tries++ < 25) setTimeout(apply, 200);
    };
    apply();
  };

  const Card = ({ icon: Icon, title, children, testid }) => (
    <div className="livret-card rounded-3xl bg-white/80 backdrop-blur ring-1 ring-brand/10 p-6 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <span className="w-11 h-11 rounded-full bg-brand text-white flex items-center justify-center shrink-0"><Icon size={22} /></span>
        <h2 className="font-heading text-xl text-brand">{title}</h2>
      </div>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg,#eaf5fb 0%,#f7fbfd 100%)" }} data-testid="livret-page">
      <Helmet><meta name="robots" content="noindex,nofollow" /><title>Livret d'accueil · La Bégaudière</title></Helmet>
      <style>{`
        .goog-te-banner-frame, .skiptranslate > iframe { display:none !important; }
        body { top:0 !important; position:static !important; }
        #google_translate_element { display:none !important; }
        @keyframes livretIn { from { opacity:0; transform: translateY(14px); } to { opacity:1; transform:none; } }
        .livret-card { animation: livretIn .55s ease both; transition: transform .2s ease, box-shadow .2s ease; }
        .livret-card:hover { transform: translateY(-3px); box-shadow: 0 12px 28px -12px rgba(20,128,179,.35); }
      `}</style>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-12">
        {/* Header */}
        <div className="text-center mb-6">
          <img src="/logo-begaudiere.webp" alt="La Bégaudière" className="h-28 sm:h-32 mx-auto mb-4 object-contain" />
          <h1 className="font-heading text-4xl text-brand">{t.welcome} !</h1>
          <p className="text-ink-500 text-sm mt-1">{L.welcome || t.welcomeMsg}</p>
        </div>

        {/* Language selector — drapeaux + toutes langues via Google Translate */}
        <div className="flex flex-col items-center gap-1.5 mb-8 notranslate" translate="no" data-testid="livret-langs">
          <span className="text-xs text-ink-400">🌍 {t.chooseLang}</span>
          <select value={curLang} onChange={(e) => translateTo(e.target.value)}
            className="rounded-full bg-white ring-1 ring-brand/20 px-4 py-2 text-sm text-ink-700 focus:outline-none focus:ring-brand/50 cursor-pointer"
            data-testid="livret-lang-select">
            {LANGS.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
          </select>
          <div id="google_translate_element" />
        </div>

        <div className="space-y-4">
          {/* Weather */}
          {weather.length > 0 && (
            <Card icon={CloudSun} title={t.weather} testid="livret-weather">
              <div className="grid grid-cols-5 gap-2">
                {weather.map((d, i) => {
                  const Ic = weatherIcon(d.code);
                  const day = new Date(d.date + "T12:00:00").toLocaleDateString(loc, { weekday: "short" });
                  return (
                    <div key={i} className="text-center rounded-xl bg-brand-50/60 py-3">
                      <p className="text-xs text-ink-500 capitalize">{day}</p>
                      <Ic size={26} className="mx-auto my-1 text-brand" />
                      <p className="text-sm font-semibold text-ink-800">{d.tmax}°</p>
                      <p className="text-xs text-ink-400">{d.tmin}°</p>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Breakfast */}
          <Card icon={Coffee} title={t.breakfast} testid="livret-breakfast">
            {L.breakfast_note ? <p className="text-sm font-medium text-brand mb-1">{L.breakfast_note}</p> : null}
            <p className="text-sm text-ink-700">{t.week} : <b>{L.breakfast_week || "8h00 – 10h00"}</b></p>
            <p className="text-sm text-ink-700">{t.weekend} : <b>{L.breakfast_weekend || "8h30 – 10h30"}</b></p>
          </Card>

          {/* Games room */}
          {L.games ? (
            <Card icon={Gamepad2} title={t.games} testid="livret-games">
              <p className="text-sm text-ink-700">{L.games}</p>
              {L.games_hours ? <p className="text-sm text-ink-500 mt-1">{L.games_hours}</p> : null}
            </Card>
          ) : null}

          {/* Wi-Fi */}
          <Card icon={Wifi} title={t.wifi} testid="livret-wifi">
            <p className="text-sm text-ink-700">SSID : <b>{L.wifi_ssid}</b></p>
            <p className="text-sm text-ink-700">{t.pass} : <b>{L.wifi_password}</b></p>
          </Card>

          {/* Door code */}
          <div className="rounded-3xl bg-brand text-white p-6 text-center shadow-md" data-testid="livret-door">
            <div className="flex items-center justify-center gap-2 mb-1"><Lock size={20} /><span className="font-heading text-lg">{t.door}</span></div>
            <p className="font-heading text-5xl tracking-widest my-2">{L.door_code || "----"}</p>
            <p className="text-xs uppercase tracking-wide text-white/80">{t.important} !</p>
          </div>

          {/* No outside food warning */}
          {L.food_warning ? (
            <div className="rounded-3xl bg-red-50 ring-2 ring-red-300 p-5 flex gap-3 items-start" data-testid="livret-nofood">
              <span className="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center shrink-0"><Ban size={22} /></span>
              <div>
                <p className="font-heading text-lg text-red-700">{t.noFood}</p>
                <p className="text-sm text-red-800/90 mt-0.5">{L.food_warning}</p>
              </div>
            </div>
          ) : null}

          {/* Reminders */}
          {(L.reminders || []).length > 0 && (
            <Card icon={ClipboardList} title={t.reminders} testid="livret-reminders">
              <p className="text-sm text-ink-500 italic mb-3">{t.respect}</p>
              <ul className="space-y-2">
                {(L.reminders || []).map((r, i) => (
                  <li key={i} className="flex gap-2 text-sm text-ink-700"><span className="text-brand mt-0.5">•</span><span>{r}</span></li>
                ))}
              </ul>
            </Card>
          )}

          {/* Suppléments & tarifs */}
          <Card icon={Euro} title={t.supplements} testid="livret-supplements">
            <ul className="space-y-3">
              {(L.supplements && L.supplements.length > 0 ? L.supplements : DEFAULT_SUPPLEMENTS).map((s, i) => (
                <li key={i} className="flex gap-3 items-start rounded-xl bg-brand-50/60 px-3 py-2.5" data-testid={`livret-supplement-${i}`}>
                  <span className="text-brand mt-0.5 shrink-0"><Euro size={16} /></span>
                  <span className="text-sm text-ink-800">{s}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* Dining + order */}
          <Card icon={UtensilsCrossed} title={t.dining} testid="livret-dining">
            <p className="text-sm text-ink-700 mb-4">{L.dining}</p>
            <a href="/commander-repas" className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-3 text-sm font-medium hover:bg-brand-600 transition-colors" data-testid="livret-order-btn">
              {t.order} <ArrowRight size={16} />
            </a>
          </Card>

          {/* Things to do nearby */}
          {(L.places || []).length > 0 && (
            <Card icon={MapPin} title={t.nearby} testid="livret-places">
              <ul className="space-y-3">
                {(L.places || []).map((p, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-800">{p.name}</p>
                      {p.desc ? <p className="text-xs text-ink-500">{p.desc}</p> : null}
                    </div>
                    {p.distance ? <span className="text-xs font-medium text-brand shrink-0 whitespace-nowrap">{p.distance}</span> : null}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Practical info */}
          <Card icon={Clock} title={t.practical} testid="livret-practical">
            <p className="text-sm text-ink-700">{t.checkout} : <b>{L.checkout || "11h00"}</b></p>
            {L.contact_phone ? <p className="text-sm text-ink-700 mt-1 flex items-center gap-1.5"><Phone size={14} className="text-brand" /> {t.contact} : <b>{L.contact_phone}</b></p> : null}
          </Card>
        </div>

        <p className="text-center text-brand/60 font-heading text-lg mt-10">La Bégaudière · labegaudiere.fr</p>
      </div>
    </div>
  );
}
